<div *ngIf="isLoading" class="overlay">
  <div class="spinner"></div>
  <span class="text-load">Loading...</span>
</div>

<!-- Breadcrumb -->
<div class="breadcrumb-bar">
  <div class="breadcrumb">
    <a class="breadcrumb-link breadcrumb-link--active" routerLink="/dashboard">Dashboard</a>
    <span class="breadcrumb-sep">›</span>
    <span class="breadcrumb-link">Admin Panel</span>
    <span class="breadcrumb-sep">›</span>
    @if (showAddUserPanel) {
      <span class="breadcrumb-link" (click)="onAddUserCancel()">User Management</span>
      <span class="breadcrumb-sep">›</span>
      <span class="breadcrumb-current">Add New User</span>
    } @else {
      <span class="breadcrumb-current">
        @if (activeTab === 'roles') { Roles &amp; Permissions }
        @else if (activeTab === 'users') { User Management }
        @else { Audit Log }
      </span>
    }
  </div>
</div>

<!-- ── Add New User screen (replaces main content) ── -->
@if (showAddUserPanel) {
  <main class="admin-main add-user-page">

    <div class="add-user-card">

      <div class="add-user-header">
        User Management &mdash; Add New User
      </div>

      <div class="add-user-body">

        @if (addUserSubmitError) {
          <div class="add-user-error-banner">{{ addUserSubmitError }}</div>
        }

        <!-- Request Type (read-only) -->
        <div class="add-user-row">
          <label class="add-user-label">Request Type</label>
          <input class="add-user-input add-user-input--readonly" type="text" value="Add New User" readonly />
        </div>

        <!-- Email / Enterprise ID -->
        <div class="add-user-row">
          <label class="add-user-label">Email / Enterprise ID <span class="add-user-required">*</span></label>
          <div class="add-user-field-wrap">
            <div (focusout)="validateNewUserEid()">
              <app-people-picker class="ppinput"
                [(ngModel)]="newUserEmail"
                placeholder="Search by Email or Enterprise ID..."
                (personSelected)="onPersonSelected($event)"
                (ngModelChange)="onNewUserEmailInput()">
              </app-people-picker>
            </div>
            <span [class.field-invalid]="showAddUserErrors && !newUserEmail.trim()"></span>
            @if (showAddUserErrors && !newUserEmail.trim()) {
              <span class="add-user-field-error">Please fill in all mandatory fields before submitting.</span>
            }
            @if (newUserEidError) {
              <span class="add-user-field-error">{{ newUserEidError }}</span>
            }
          </div>
        </div>

        <!-- First Name -->
        <div class="add-user-row">
          <label class="add-user-label">First Name <span class="add-user-required">*</span></label>
          <div class="add-user-field-wrap">
            <input class="add-user-input" type="text" placeholder="Enter first name"
                   [class.field-invalid]="showAddUserErrors && !newUserFirstName.trim()"
                   [readonly]="newUserEidAutoFilled"
                   [class.add-user-input--readonly]="newUserEidAutoFilled"
                   [(ngModel)]="newUserFirstName" />
            @if (showAddUserErrors && !newUserFirstName.trim()) {
              <span class="add-user-field-error">Please fill in all mandatory fields before submitting.</span>
            }
          </div>
        </div>

        <!-- Last Name -->
        <div class="add-user-row">
          <label class="add-user-label">Last Name <span class="add-user-required">*</span></label>
          <div class="add-user-field-wrap">
            <input class="add-user-input" type="text" placeholder="Enter last name"
                   [class.field-invalid]="showAddUserErrors && !newUserLastName.trim()"
                   [readonly]="newUserEidAutoFilled"
                   [class.add-user-input--readonly]="newUserEidAutoFilled"
                   [(ngModel)]="newUserLastName" />
            @if (showAddUserErrors && !newUserLastName.trim()) {
              <span class="add-user-field-error">Please fill in all mandatory fields before submitting.</span>
            }
          </div>
        </div>

        <!-- R&C Function (Designation) -->
        <div class="add-user-row">
          <label class="add-user-label">R&amp;C Function (Designation) <span class="add-user-required">*</span></label>
          <div class="add-user-field-wrap">
            <select class="add-user-select"
                    [class.field-invalid]="showAddUserErrors && !newUserDesignation"
                    [(ngModel)]="newUserDesignation">
              <option value="" disabled>-- Select Designation --</option>
              @for (opt of designationOptionsForAdmin; track opt) {
                <option [value]="opt">{{ opt }}</option>
              }
            </select>
            @if (showAddUserErrors && !newUserDesignation) {
              <span class="add-user-field-error">Please fill in all mandatory fields before submitting.</span>
            }
          </div>
        </div>

        <!-- Country -->
        <div class="add-user-row">
          <label class="add-user-label">Country <span class="add-user-required">*</span></label>
          <div class="add-user-field-wrap">
            <select class="add-user-select"
                    [class.field-invalid]="showAddUserErrors && !newUserCountry"
                    [(ngModel)]="newUserCountry"
                    (ngModelChange)="onAdminCountryChange()">
              <option value="" disabled>Select Country</option>
              @if (isLoadingCountriesForAdmin) {
                <option disabled>Loading...</option>
              }
              @for (c of countryListForAdmin; track c.id) {
                <option [value]="c.name">{{ c.name }}</option>
              }
            </select>
            @if (showAddUserErrors && !newUserCountry) {
              <span class="add-user-field-error">Please fill in all mandatory fields before submitting.</span>
            }
          </div>
        </div>

        <!-- Work Location -->
        <div class="add-user-row">
          <label class="add-user-label">Work Location <span class="add-user-required">*</span></label>
          <div class="add-user-field-wrap">
            @if (isLoadingLocationsForAdmin) {
              <div class="add-user-loading-wrap">
                <div class="add-user-spinner"></div>
                <span class="add-user-loading-text">Loading locations...</span>
              </div>
            } @else {
              <select class="add-user-select"
                      [class.field-invalid]="showAddUserErrors && !newUserWorkLocation"
                      [disabled]="!newUserCountry"
                      [(ngModel)]="newUserWorkLocation">
                <option value="" disabled>Select Work Location</option>
                @for (loc of workLocationListForAdmin; track loc) {
                  <option [value]="loc">{{ loc }}</option>
                }
              </select>
            }
            @if (showAddUserErrors && !newUserWorkLocation && !isLoadingLocationsForAdmin) {
              <span class="add-user-field-error">Please fill in all mandatory fields before submitting.</span>
            }
          </div>
        </div>

        <!-- Expiration On (optional) -->
        <div class="add-user-row">
          <label class="add-user-label">Expiration On</label>
          <div class="add-user-field-wrap">
            <div class="add-user-input add-user-date-input admin-date-picker-wrap"
                 (click)="openDatePicker(adminPicker_expiry)">
              <span class="rc-date-display">{{ formatDateDisplay(newUserExpirationOn) || 'DD-MMM-YYYY' }}</span>
              <svg class="rc-date-cal-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="16" y1="2" x2="16" y2="6"/></svg>
              <input #adminPicker_expiry type="date" class="rc-date-input-hidden"
                     [(ngModel)]="newUserExpirationOn"
                     [min]="todayDateStrAdmin" />
            </div>
            <p class="add-user-hint">Optional &mdash; leave blank for permanent access</p>
          </div>
        </div>

        <!-- Active / Inactive -->
        <div class="add-user-row">
          <label class="add-user-label">Active / Inactive <span class="add-user-required">*</span></label>
          <div class="add-user-field-wrap">
            <select class="add-user-select" [(ngModel)]="newUserStatus">
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        <!-- Notify to User (optional) -->
        <div class="add-user-row">
          <label class="add-user-label">Notify to User</label>
          <div class="add-user-field-wrap">
            <input class="add-user-input" type="text" placeholder="Search by name or EID"
                   [(ngModel)]="newUserNotify" />
            <p class="add-user-hint">Optional &mdash; separate multiple names with a comma</p>
          </div>
        </div>

      </div>

      <!-- Actions -->
      <div class="add-user-actions">
        <button class="add-user-btn-cancel" type="button" (click)="onAddUserCancel()">Cancel</button>
        <button class="add-user-btn-submit" type="button" (click)="onAddUserSubmit()">Submit</button>
      </div>

    </div>
  </main>
}

<!-- ── Normal admin screen ── -->
@if (!showAddUserPanel) {
  <main class="admin-main">
    <!-- Tab bar -->
    <div class="tab-bar">
      <div class="tabs">
        <button class="tab-btn" [class.active]="activeTab === 'roles'" (click)="setTab('roles')">
          <span class="tab-icon">&#128274;</span>
          Roles &amp; Permissions
        </button>
        <button class="tab-btn" [class.active]="activeTab === 'users'" (click)="setTab('users')">
          <span class="tab-icon">&#128100;</span>
          User Management
        </button>
        <button class="tab-btn" [class.active]="activeTab === 'audit'" (click)="setTab('audit')">
          <span class="tab-icon">&#128203;</span>
          Audit Log
        </button>
      </div>
      @if (activeTab === 'roles') {
        <button class="btn-add-role" (click)="onAddNewRole()">+ Add New Role</button>
      }
      @if (activeTab === 'users') {
        <button class="btn-add-role" (click)="onAddNewUser()">+ Add New User</button>
      }
    </div>

    <!-- Roles & Permissions tab -->
    @if (activeTab === 'roles') {
      <div class="panel-body">
        <div class="toolbar">
          <div class="search-wrap">
            <span class="search-icon">&#128269;</span>
            <input class="search-input" type="text" placeholder="Search roles..." [(ngModel)]="searchQuery" />
          </div>
          <div class="filter-group">
            <button class="filter-btn" [class.active]="activeFilter === 'All'" (click)="setFilter('All')">All</button>
            <button class="filter-btn" [class.active]="activeFilter === 'Base Role'" (click)="setFilter('Base Role')">Base Role</button>
            <button class="filter-btn" [class.active]="activeFilter === 'Custom Role'" (click)="setFilter('Custom Role')">Custom Role</button>
          </div>
        </div>

        <div class="roles-table-wrap">
        <table class="roles-table">
          <thead>
            <tr>
              <th>ROLE NAME</th>
              <th>TYPE</th>
              <th>DESCRIPTION</th>
              <th>USERS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            @for (role of filteredRoles; track role.name) {
              <tr>
                <td class="role-name">{{ role.name }}</td>
                <td>
                  <span class="role-badge" [class.base]="!role.isCustom" [class.custom]="role.isCustom">{{ role.type }}</span>
                </td>
                <td class="role-desc">{{ role.description }}</td>
                <td class="role-users">{{ role.users }}<br /><span class="users-label">users</span></td>
                <td class="role-actions">
                  <div class="actions-row">
                    <button class="action-btn" (click)="onView(role)">View</button>
                    @if (role.isCustom) {
                      <button class="action-btn edit" (click)="onEdit(role)">Edit</button>
                    }
                  </div>
                  @if (role.isCustom) {
                    <div class="actions-row">
                      <button class="action-btn delete" (click)="onDelete(role)">Delete</button>
                    </div>
                  }
                </td>
              </tr>
            }
            @if (filteredRoles.length === 0) {
              <tr><td colspan="5" class="no-results">No roles match your search.</td></tr>
            }
          </tbody>
        </table>
        </div>

        <div class="table-footer">
          {{ roles.length }} roles &middot; {{ baseRoleCount }} base &middot; {{ customRoleCount }} custom
        </div>
      </div>
    }

    <!-- User Management tab -->
    @if (activeTab === 'users') {
      <div class="panel-body">
        <div class="toolbar">
          <div class="search-wrap">
            <span class="search-icon">&#128269;</span>
            <input class="search-input" type="text" placeholder="Search users..." [(ngModel)]="userSearchQuery" />
          </div>
          <div class="user-filters">
            <select class="filter-select" [(ngModel)]="selectedRoleFilter">
              <option value="All Roles">All Roles</option>
              <option value="R&C Member">R&amp;C Member</option>
              <option value="Reviewer">Reviewer</option>
              <option value="Supervisor">Supervisor</option>
              <option value="Manager">Manager</option>
              <option value="Administrator">Admin</option>
            </select>
            <select class="filter-select" [(ngModel)]="selectedStatusFilter">
              <option>All Status</option>
              <option>Active</option>
              <option>Inactive</option>
            </select>
          </div>
        </div>

        <div class="users-table-wrap">
          <table class="users-table">
            <thead>
              <tr>
                <th>NAME</th>
                <th>EMAIL / EID</th>
                <th>ROLE(S)</th>
                <th>COUNTRY</th>
                <th>STATUS</th>
                <th>EXPIRY</th>
                <th>ACT AS REVIEWER</th>
                <th>ACT AS SUPERVISOR</th>
                <th>ACT AS MANAGER</th>
                <th>REQUESTED BY</th>
                <th>CREATED DATE</th>
                <th>UPDATED DATE</th>
                <th class="sticky-col">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              @for (user of pagedUsers; track user.email) {
                <tr>
                  <td class="user-name-cell">
                    <span class="user-name">{{ user.name }}</span>
                    <span class="user-subtitle">{{ user.subtitle }}</span>
                  </td>
                  <td class="user-email">{{ user.email }}</td>
                  <td>
                    @for (role of user.roles; track role) {
                      <span class="user-role-badge">{{ role }}</span>
                    }
                  </td>
                  <td>{{ user.country }}</td>
                  <td>
                    <span class="status-badge" [class.active]="user.status === 'Active'" [class.inactive]="user.status === 'Inactive'">
                      {{ user.status }}
                    </span>
                  </td>
                  <td>{{ formatDateDisplay(user.expiry) }}</td>
                  <td class="count-cell">{{ user.actAsReviewer }}</td>
                  <td class="count-cell">{{ user.actAsSupervisor }}</td>
                  <td class="count-cell">{{ user.actAsManager }}</td>
                  <td class="requested-by-cell">{{ user.requestedBy ?? '' }}</td>
                  <td class="date-cell">{{ user.createdDate ? (user.createdDate | date:'dd-MMM-yyyy') : '' }}</td>
                  <td class="date-cell">{{ user.updatedDate ? (user.updatedDate | date:'dd-MMM-yyyy') : '' }}</td>
                  <td class="user-actions-cell sticky-col">
                    <button class="user-action-btn view" title="View" (click)="onViewUser(user)">
                      <i class="fa fa-eye"></i>
                    </button>
                    <button class="user-action-btn edit" title="Edit" (click)="onEditUser(user)">
                      <i class="fa fa-pencil"></i>
                    </button>
                    <button class="user-action-btn delete" title="Delete" (click)="onDeleteUser(user)">
                      <i class="fa fa-trash"></i>
                    </button>
                  </td>
                </tr>
              }
              @if (pagedUsers.length === 0) {
                <tr><td colspan="13" class="no-results">No users found.</td></tr>
              }
            </tbody>
          </table>
        </div>

        <div class="user-pagination">
          <button class="pag-btn" [disabled]="userCurrentPage === 1" (click)="userCurrentPage = userCurrentPage - 1">&#8249;</button>
          <span class="pag-info">Page {{ userCurrentPage }} of {{ userTotalPages }}</span>
          <button class="pag-btn" [disabled]="userCurrentPage === userTotalPages" (click)="userCurrentPage = userCurrentPage + 1">&#8250;</button>
        </div>
      </div>
    }

    <!-- Audit Log tab -->
    @if (activeTab === 'audit') {
      <div class="panel-body">
        <app-audit-log-grid />
      </div>
    }
  </main>
}
